import axios, { AxiosResponse, CancelToken } from "axios"
import { documentoComercialValidacionAdapter } from "./adapter_globals/DocumentoComercialValidacion.adapter"
import { DocumentoComercialAsociadoReadingDtoProps } from "./DTOs/DocumentoComercialAsociadoReadingDto"
import { DocumentoComercialValidacionDtoProps } from "./DTOs/DocumentoComercialValidacionDto"
import { FicheroCreationDtoProps } from "./DTOs/FicheroCreationDto"
import { FicheroReadingDtoProps } from "./DTOs/FicheroReadingDto"
import { PedidoCreationDtoProps } from "./DTOs/PedidoCreationDto"
import { PedidoReadingDtoProps } from "./DTOs/PedidoReadingDto"
import { PedidoReadingFiltersDtoProps } from "./DTOs/PedidoReadingFiltersDto"
import { PedidoReadingSimpleDtoProps } from "./DTOs/PedidoReadingSimpleDto"
import { ValidacionDocumentoReadingDtoProps } from "./DTOs/ValidacionDocumentoReadingDto"
import { DocumentoComercialValidacionDtoQueryParamsProps } from "./interfaces/DocumentoComercialValidacionDtoQueryParamsProps"
import { SortingCriteria } from "./interfaces/SortingCriteria"
import { PedidoModelProps } from "./Models/PedidoModel"
import {
  pedidoByFilterAdapter,
  pedidoById,
  pedidoByIdSimple,
  pedidoDocAsociadosAdapter,
  pedidoDocumentoPendienteValidacionAdapter,
  pedidoFicherosAdjuntosAdapter,
} from "./Pedido.adapter"
import { PedidoReadingFiltersDtoIPagedEnumerable } from "./Schemas/PedidoReadingFiltersDtoIPagedEnumerable"
import { EstadoRevisionDocumentoComercialType } from "./Types/EstadoRevisionDocumentoComercial"
import { TipoDocumentoComercialType } from "./Types/TipoDocumentoComercial"

export interface ByFilterPedidosQueryParamsProps {
  EmailLibrado?: string
  EmpresaActivaIdUsuario?: string
  EstadoRevision?: EstadoRevisionDocumentoComercialType[]
  ClienteNif?: string
  ProveedorNif?: string
  FechaEmisionLowerLimit?: string
  FechaEmisionUpperLimit?: string
  ImporteLowerLimit?: number | string
  ImporteUpperLimit?: number | string
  LibradoId?: string
  LibradorId?: string
  MaxResultCount?: number | string
  MsgEncrypted?: string
  SkipCount?: number
  SortingCriteria: SortingCriteria
  Keyword?: string
}

export async function getPedidosByFilter(
  queryParams: ByFilterPedidosQueryParamsProps,
  cancelToken?: CancelToken
): Promise<PedidoReadingFiltersDtoIPagedEnumerable> {
  const { data }: { data: PedidoReadingFiltersDtoIPagedEnumerable } =
    await axios.get("/Pedido/by-filters", {
      params: queryParams,
      cancelToken,
    })
  return {
    currentPage: data.currentPage,
    items: data.items?.map((pedido: PedidoReadingFiltersDtoProps) =>
      pedidoByFilterAdapter(pedido)
    ),
    totalCount: data.totalCount,
  }
}

/**
 * @description Check if User has any Pedido
 * @returns {Boolean}
 */
export async function getUnsignedPedido(
  queryParams?: DocumentoComercialValidacionDtoQueryParamsProps
): Promise<Array<DocumentoComercialValidacionDtoProps>> {
  const {
    data,
  }: Record<
    "data",
    Array<DocumentoComercialValidacionDtoProps>
  > = await axios.get("/Pedido/pendientes-firmar", {
    params: queryParams,
  })
  return data.map((documento: DocumentoComercialValidacionDtoProps) =>
    documentoComercialValidacionAdapter(documento)
  )
}

export async function postValidatePedidoPendiente(id: string): Promise<void> {
  await axios.post(`/pedido/${id}/validar`)
}

export async function getPedidoPdfById(id: string) {
  const { data, status } = await axios.get(`/Pedido/${id}/pdf`, {
    responseType: "blob",
  })
  return { data, status }
}

export async function getPedidoEdi(id: string, formato: number) {
  const { data } = await axios.get(
    `/Pedido/export-edi/${id}/formato/${formato}`,
    {
      responseType: "blob",
    }
  )

  return data
}

export async function postPedidoPdf(documento: PedidoModelProps) {
  const { data } = await axios.post("/Pedido/generate-pdf", documento, {
    responseType: "blob",
  })
  return data
}

export async function postPedido(documento: PedidoCreationDtoProps) {
  const resp = await axios.post("/Pedido", documento)
  return resp
}

export async function getPedidoByIdSimple(
  id: string
): Promise<PedidoReadingSimpleDtoProps> {
  const { data } = await axios.get(`/Pedido/${id}/simple`)
  return pedidoByIdSimple(data)
}

export async function getPedidoById(
  id: string
): Promise<PedidoReadingDtoProps> {
  const { data } = await axios.get(`/Pedido/${id}`)
  return pedidoById(data)
}

export async function getPedidoLastAvailableNumero(
  serie: string
): Promise<number> {
  const response = await axios.get(`/Pedido/last-available-numero/${serie}`)
  return response.data
}

export async function getPedidoDocsAsociadosById(
  id: string
): Promise<DocumentoComercialAsociadoReadingDtoProps[]> {
  const { data } = await axios.get(`/Pedido/${id}/documentos-asociados`)
  return data.map((element: DocumentoComercialAsociadoReadingDtoProps) =>
    pedidoDocAsociadosAdapter(element)
  )
}
export async function getPedidoFicherosAdjuntosId(
  id: string
): Promise<FicheroReadingDtoProps[]> {
  const { data } = await axios.get(`/Pedido/${id}/ficheros-adjuntos`)
  return data.map((element: FicheroReadingDtoProps) =>
    pedidoFicherosAdjuntosAdapter(element)
  )
}

export async function postPresupuestoGenerateExcel(queryParams: {
  tipoDocumentoComercial: TipoDocumentoComercialType
}): Promise<AxiosResponse<Blob>> {
  return axios.get("/Pedido/exportar-excel", {
    params: queryParams,
    responseType: "blob",
  })
}

export async function getPedidoDocumentoPendienteValidacionDocumentoPendienteValidacion(
  id: string
): Promise<ValidacionDocumentoReadingDtoProps> {
  const { data } = await axios.get(`/Pedido/${id}/validacion`)
  return pedidoDocumentoPendienteValidacionAdapter(data)
}

export async function deletePedidoArichivoAdjunto(
  presupuestoID: string,
  fileID: string
) {
  return axios.delete(`/Pedido/${presupuestoID}/fichero/${fileID}`)
}

export async function postPedidoAñadirArchivoAdjunto(
  presupuestoID: string,
  file: FicheroCreationDtoProps
) {
  return axios.post(`/Pedido/${presupuestoID}/add-fichero-adjunto`, file)
}
